/**
 * MediaPanel — the swipe-DOWN vertical surface (framework: Personal up / Media
 * down). "Media" here is Evan's own captured audio life-stream: recent Limitless
 * lifelogs via sage-limitless-mcp. Real media, not a stub. Tap an item to pull
 * its transcript/summary into the central chat.
 */
import { useMemo } from "react";
import { AudioLines, Clock, Radio } from "lucide-react";
import McpCard from "../components/McpCard";
import { useMcpTool } from "../lib/useMcpTool";
import { PanelScroll } from "./ClientsPanel";
import { setChatContext } from "../lib/chatContext";

interface LLContent { content?: string; type?: string; startTime?: string; endTime?: string; speakerName?: string }
interface Lifelog { id?: string; title?: string; summary?: string; markdown?: string; startTime?: string; endTime?: string; start_time?: string; created_at?: string; contents?: LLContent[]; [k: string]: unknown }

// sage-limitless list_recent returns { data: { lifelogs: [...] } }; each lifelog
// carries a `contents` array of heading/blockquote nodes rather than title/summary.
function logsFrom(d: unknown): Lifelog[] {
  if (Array.isArray(d)) return d as Lifelog[];
  if (d && typeof d === "object") {
    const o = d as Record<string, unknown>;
    const nested = (o.data as Record<string, unknown> | undefined)?.lifelogs;
    if (Array.isArray(nested)) return nested as Lifelog[];
    for (const k of ["lifelogs", "results", "rows", "items", "recent"]) {
      if (Array.isArray(o[k])) return o[k] as Lifelog[];
    }
  }
  return [];
}
const heading = (l: Lifelog) => l.contents?.find((c) => c.type === "heading1")?.content
  ?? l.contents?.find((c) => c.type === "heading2")?.content;
const startOf = (l: Lifelog) => l.startTime ?? l.start_time ?? l.created_at ?? l.contents?.find((c) => c.startTime)?.startTime ?? "";
function fmt(s: string): string {
  if (!s) return "";
  const d = new Date(s); if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
function dur(l: Lifelog): string {
  const a = Date.parse(startOf(l)); const b = Date.parse(l.endTime ?? "");
  if (Number.isNaN(a) || Number.isNaN(b) || b <= a) return "";
  const m = Math.round((b - a) / 60000); return m < 60 ? `${m}m` : `${(m / 60).toFixed(1)}h`;
}
const titleOf = (l: Lifelog) => l.title ?? heading(l) ?? "Untitled recording";
const summaryOf = (l: Lifelog) => (l.summary ?? l.markdown
  ?? (l.contents ?? []).filter((c) => c.content && c.type !== "heading1").map((c) => c.content).join(" ").slice(0, 220))
  || "";

export default function MediaPanel() {
  const recent = useMcpTool<unknown>("limitless", "list_recent", { limit: 30 }, { timeoutMs: 25_000 });
  const logs = useMemo(() => [...logsFrom(recent.data)].sort((a, b) => (startOf(b) > startOf(a) ? 1 : -1)), [recent.data]);

  return (
    <PanelScroll accentVar="--biome-mindvault" title="Media" subtitle="captured audio life-stream · Limitless">
      <div className="flex items-center gap-2 text-caption text-text-dim -mt-1"><Radio size={13} className="text-primary" /> swipe up to return · tap a recording to discuss it</div>
      <McpCard title={`Recent recordings${logs.length ? ` (${logs.length})` : ""}`} loading={recent.loading} error={recent.error} onRefresh={recent.refresh} accentVar="--biome-mindvault">
        {logs.length === 0 ? (
          <div className="text-body-sm text-text-dim flex items-center gap-2"><Clock size={14} /> No recent lifelogs (or limitless token not imported).</div>
        ) : (
          <div className="space-y-1.5">
            {logs.map((l, i) => (
              <button key={l.id ?? i}
                onClick={() => setChatContext({ label: `Recording: ${titleOf(l).slice(0, 40)}`, detail: `${titleOf(l)} (${fmt(startOf(l))})\n${summaryOf(l).slice(0, 1200)}` })}
                className="w-full flex items-start gap-3 text-left px-3 py-2.5 rounded-lg border bg-surface hover:bg-surface-hover hover:border-primary/40 transition-colors group">
                <span className="mt-0.5 w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "color-mix(in srgb, var(--biome-mindvault) 18%, transparent)", color: "var(--biome-mindvault)" }}>
                  <AudioLines size={16} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-body-sm text-text font-medium leading-snug group-hover:text-primary transition-colors line-clamp-1">{titleOf(l)}</span>
                  {summaryOf(l) && <span className="block text-caption text-text-muted leading-snug line-clamp-2 mt-0.5">{summaryOf(l).replace(/[#*_>`]/g, "").slice(0, 160)}</span>}
                  <span className="block text-caption text-text-dim font-mono mt-1">{fmt(startOf(l))}{dur(l) ? ` · ${dur(l)}` : ""}</span>
                </span>
              </button>
            ))}
          </div>
        )}
      </McpCard>
    </PanelScroll>
  );
}
